import { useState } from 'react';
import { STAGING_LOAN } from '../../config/env';
import { numberFormatter } from 'components/misc/constants';
import { useUser } from 'hooks';
import { fetchRequests } from 'https/requests';
import { show } from 'providers/toastSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle01Icon, Cancel01Icon, Alert01Icon, MoneyReceive02Icon, ChartBarLineIcon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import ReviewModal from './ReviewModal';

function StatRow({ label, value, highlight }) {
    return (
        <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-white/[.06] last:border-0">
            <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
            <span className={`text-sm font-semibold ${highlight ? 'text-amber-600 dark:text-amber-400' : 'text-gray-800 dark:text-gray-200'}`}>{value}</span>
        </div>
    );
}

function EligCheck({ status, data, loanType, closeModal, setReviewModalOpen }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { accessToken } = useUser();
    const [processing, setProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const handleRequest = async (e) => {
        e.preventDefault();
        setProcessing(true);
        if (error) setError(false);

        if (data.loanAmt === 0) {
            setError('Your eligible amount is zero (GHS 0). You need a minimum eligible amount of GHS 1. Please try again later.');
            setProcessing(false);
            return;
        }

        let url = `${STAGING_LOAN}/apply/${loanType}`;
        let headers = {
            'authorization': `${accessToken}`,
            'Content-Type': 'multipart/form-data',
        };

        const formData = new FormData();
        for (const [key, value] of Object.entries(data?.data)) {
            if (key === 'otherDocs') {
                value.map((item, index) => formData.append(`otherDocument_${index + 1}`, item));
            }
            formData.append(`${key}`, value);
        }

        let res = await fetchRequests(url, 'post', headers, {}, formData);
        setProcessing(false);

        if (res?.code === 401) {
            navigate('/login');
            closeModal();
            dispatch(show({ state: true, message: 'Your session has expired, please login to continue.', type: 'token' }));
            return;
        }

        // Failure can arrive as { err } (business/validation, 400) or
        // { error } (unhandled server error, 500). Treat both as failures —
        // never advance to the review modal on an error response.
        if (res?.err || res?.error) {
            setError(res?.err || res?.error);
            return;
        }

        // A genuine success always carries a message; anything else is a
        // failed/empty response that must not be reported as success.
        if (!res?.message) {
            setError('Submission failed. Please try again.');
            return;
        }

        setSuccess(res.message);
        setTimeout(() => closeModal(), 1000);
        setTimeout(() => setReviewModalOpen(true), 1300);
    };

    if (status !== 'accepted') {
        return (
            <div className="flex items-start gap-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-4 py-4 mt-2">
                <HugeiconsIcon icon={Cancel01Icon} size={18} className="text-red-500 shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-semibold text-red-700 dark:text-red-400">Application not accepted</p>
                    <p className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">Unfortunately your loan application did not pass our pre-check. Please review your information and try again.</p>
                </div>
            </div>
        );
    }

    return (
        <>
        <ReviewModal isOpen={isOpen} isClose={setIsOpen} client={data?.data?.signedName} />

        <div className="mt-3 space-y-4">
            {/* Key metrics */}
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 px-4 py-3.5">
                    <div className="flex items-center gap-2 mb-1">
                        <HugeiconsIcon icon={MoneyReceive02Icon} size={14} className="text-amber-500" />
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Eligible Amount</p>
                    </div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">GHS {numberFormatter(data?.loanAmt)}</p>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 px-4 py-3.5">
                    <div className="flex items-center gap-2 mb-1">
                        <HugeiconsIcon icon={ChartBarLineIcon} size={14} className="text-emerald-500" />
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Credit Score</p>
                    </div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{data?.score}%</p>
                </div>
            </div>

            {/* Breakdown */}
            <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 px-4 py-1">
                <StatRow label="Monthly Interest Rate" value={`${data?.interestRate}%`} />
                <StatRow label="Processing fee (one-time)" value={`GHS ${numberFormatter(data?.paymentObj?.processingFee)}`} />
                <StatRow label="Insurance fee (one-time)" value={`GHS ${numberFormatter(data?.paymentObj?.insuranceFee)}`} />
                <StatRow label="Amount to receive" value={`GHS ${numberFormatter(data?.paymentObj?.amountGiven)}`} highlight />
                <StatRow label="Repayment frequency" value={data?.paymentFreq} />
                <StatRow label="Repayment schedule" value={`${data?.paymentObj?.numPayments} × GHS ${numberFormatter(data?.paymentObj?.premium)}`} highlight />
            </div>

            {error && (
                <div className="flex items-start gap-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-4 py-3">
                    <HugeiconsIcon icon={Alert01Icon} size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
            )}

            {success && (
                <div className="flex items-start gap-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 px-4 py-3">
                    <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">{success}</p>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1">
                <button
                    type="button"
                    onClick={handleRequest}
                    disabled={processing}
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {processing ? (
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    ) : (
                        <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
                    )}
                    {processing ? 'Submitting…' : 'Submit application'}
                </button>
                <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
        </>
    );
}

export default EligCheck;
